/**
 * Governed pricing plan display — local i18n copy only; API supplies economics + limits.
 */

import { Building2, Camera, Crown, MapPin, Shield, Zap } from 'lucide-react';
import enUS from '../i18n/locales/en-US.json';
import deDE from '../i18n/locales/de-DE.json';
import { resolveSupportedLocale } from './supportedLocales';

export const PRICING_PLAN_IDS = ['RECON', 'OUTPOST', 'BASECAMP', 'COMMAND', 'FORTRESS'];

export const PRICING_PLANS_GRID_TEST_ID = 'pricing-plans-grid';
export const PRICING_PLAN_CARD_TEST_ID_PREFIX = 'pricing-plan-card-';

/** English API copy markers — must never appear in DE card text. */
export const API_PLAN_COPY_EN_MARKERS = [
  'Scout the Territory',
  'Premium Single Property',
  'Perfect for getting started',
  'Showcase one premium property',
  'Establish Your Presence',
  'Everything you need to professionally',
  'Take Control',
  'Professional tools for serious',
  'Build Your Empire',
  'Enterprise-grade solution',
];

const LOCALE_BUNDLES = { en: enUS, de: deDE };

export const PRICING_PLAN_ICONS = {
  RECON: Zap,
  OUTPOST: MapPin,
  BASECAMP: Shield,
  COMMAND: Crown,
  FORTRESS: Building2,
};

export const PRICING_PLAN_GRADIENTS = {
  RECON: 'from-slate-500 to-slate-600',
  OUTPOST: 'from-cyan-500 to-cyan-600',
  BASECAMP: 'from-blue-500 to-blue-600',
  COMMAND: 'from-amber-500 to-amber-600',
  FORTRESS: 'from-purple-500 to-purple-600',
};

export const PRICING_PLANS_GRID_CLASS =
  'grid w-full max-w-full grid-cols-1 gap-6 pt-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 items-stretch overflow-x-hidden';

export function pricingPlanCardTestId(planId) {
  return `${PRICING_PLAN_CARD_TEST_ID_PREFIX}${String(planId || '').toLowerCase()}`;
}

export function normalizePlanId(value) {
  return String(value || '').trim().toUpperCase();
}

export function resolvePlanId(planKey) {
  const id = normalizePlanId(planKey);
  return PRICING_PLAN_IDS.includes(id) ? id : id;
}

/** Strip API marketing copy — keep economics, limits, flags only. */
export function extractApiPlanPackage(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      price_monthly: 0,
      price_yearly: 0,
      features: {},
      popular: false,
      comparison: null,
    };
  }
  return {
    price_monthly: Number(raw.price_monthly) || 0,
    price_yearly: Number(raw.price_yearly) || 0,
    features: raw.features && typeof raw.features === 'object' ? raw.features : {},
    popular: Boolean(raw.popular),
    comparison: raw.comparison && typeof raw.comparison === 'object' ? raw.comparison : null,
  };
}

function copyKey(planId, field) {
  return `pricing.plan.${normalizePlanId(planId).toLowerCase()}.${field}`;
}

function localeBundle(locale, translations) {
  if (translations && typeof translations === 'object') {
    return translations;
  }
  const loc = resolveSupportedLocale(locale);
  return LOCALE_BUNDLES[loc] || enUS;
}

/** Plan card title/tagline/description — local JSON only, never API fields. */
export function getPricingPlanCopy(planId, locale, translations) {
  const id = resolvePlanId(planId);
  const bundle = localeBundle(locale, translations);

  return {
    planId: id,
    name: bundle[copyKey(id, 'name')] || '',
    tagline: bundle[copyKey(id, 'tagline')] || '',
    description: bundle[copyKey(id, 'description')] || '',
  };
}

export function containsApiEnglishPlanCopy(text) {
  const value = String(text || '');
  return API_PLAN_COPY_EN_MARKERS.some((marker) => value.includes(marker));
}

export function sortPlanEntries(packages) {
  return Object.entries(packages || {}).sort(([a], [b]) => {
    const ai = PRICING_PLAN_IDS.indexOf(normalizePlanId(a));
    const bi = PRICING_PLAN_IDS.indexOf(normalizePlanId(b));
    const aRank = ai === -1 ? PRICING_PLAN_IDS.length : ai;
    const bRank = bi === -1 ? PRICING_PLAN_IDS.length : bi;
    return aRank - bRank;
  });
}

export function getPricingPlanCardShellClass({ isPopular, isCurrentPlan }) {
  const parts = [
    'relative flex h-full min-w-0 flex-col rounded-xl border bg-white p-6 shadow-sm transition-shadow',
    isPopular
      ? 'z-[1] border-military-gold shadow-md shadow-military-gold/15 ring-2 ring-military-gold/25'
      : 'border-slate-200',
  ];
  if (isCurrentPlan && !isPopular) {
    parts.push('border-signal-blue ring-1 ring-signal-blue/20');
  }
  return parts.join(' ');
}

export function formatPricingFeatureValue(value, featureLabel, t) {
  if (value === true) return featureLabel;
  if (value === 'unlimited') {
    return `${t('pricing.feature.unlimited', 'Unlimited')} ${featureLabel}`;
  }
  if (typeof value === 'number' && value > 0) {
    return `${value} ${featureLabel}`;
  }
  if (typeof value === 'string' && value !== 'none') {
    return `${value} ${featureLabel}`;
  }
  return featureLabel;
}

export function isPricingFeatureIncluded(value) {
  return value === true
    || (typeof value === 'number' && value > 0)
    || (typeof value === 'string' && value !== 'none' && value !== '');
}

export function getMonthlyEquivalent(apiPlan, billingCycle) {
  if (billingCycle === 'yearly' && apiPlan.price_yearly > 0) {
    return Math.round(apiPlan.price_yearly / 12);
  }
  return apiPlan.price_monthly;
}
